import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useProductContext } from "../../context/ProductContext";
import { BranchProduct, Category } from "../../types";

const CategoriesSection: React.FC = () => {
  const { categories, products, loading: productLoading } = useProductContext();
  const navigate = useNavigate();

  const [currentImageIndex, setCurrentImageIndex] = useState<
    Record<string, number>
  >({});
  const [fadeState, setFadeState] = useState<Record<string, boolean>>({});
  const [isSectionOpen, setIsSectionOpen] = useState(false);

  // 🔍 فلترة المنتجات حسب الكاتيجوري
  const categoryProductsMap = useMemo(() => {
    const map: Record<number, BranchProduct[]> = {};
    for (const p of products) {
      const catId = p.product?.categories?.[0]?.id;
      if (catId) {
        if (!map[catId]) map[catId] = [];
        map[catId].push(p);
      }
    }
    return map;
  }, [products]);

  // ⏱️ انميشن الصور لكل كاتيجوري
  useEffect(() => {
    if (!isSectionOpen) return;

    const interval = setInterval(() => {
      categories.forEach((cat) => {
        const catId = cat.id.toString();
        const items = categoryProductsMap[cat.id] || [];
        const totalItems = cat.image ? items.length + 1 : items.length;

        if (totalItems > 1) {
          setFadeState((prev) => ({ ...prev, [catId]: false }));
          setTimeout(() => {
            setCurrentImageIndex((prev) => ({
              ...prev,
              [catId]: ((prev[catId] || 0) + 1) % totalItems,
            }));
            setFadeState((prev) => ({ ...prev, [catId]: true }));
          }, 400);
        }
      });
    }, 1100);

    return () => clearInterval(interval);
  }, [categories, categoryProductsMap, isSectionOpen]);

  // 🎨 ألوان متنوعة للخلفيات - تدرجات أنثوية
  const categoryColors = [
    {
      bg: "bg-gradient-to-br from-pink-500 via-rose-400 to-pink-300",
      text: "text-white",
      hover: "hover:from-pink-600 hover:via-rose-500 hover:to-pink-400",
      shadow: "shadow-pink-200",
    },
    {
      bg: "bg-gradient-to-br from-purple-500 via-violet-400 to-purple-300",
      text: "text-white",
      hover: "hover:from-purple-600 hover:via-violet-500 hover:to-purple-400",
      shadow: "shadow-purple-200",
    },
    {
      bg: "bg-gradient-to-br from-fuchsia-500 via-pink-400 to-fuchsia-300",
      text: "text-white",
      hover: "hover:from-fuchsia-600 hover:via-pink-500 hover:to-fuchsia-400",
      shadow: "shadow-fuchsia-200",
    },
    {
      bg: "bg-gradient-to-br from-rose-500 via-pink-400 to-rose-300",
      text: "text-white",
      hover: "hover:from-rose-600 hover:via-pink-500 hover:to-rose-400",
      shadow: "shadow-rose-200",
    },
    {
      bg: "bg-gradient-to-br from-violet-500 via-purple-400 to-violet-300",
      text: "text-white",
      hover: "hover:from-violet-600 hover:via-purple-500 hover:to-violet-400",
      shadow: "shadow-violet-200",
    },
    {
      bg: "bg-gradient-to-br from-pink-400 via-rose-300 to-pink-200",
      text: "text-gray-800",
      hover: "hover:from-pink-500 hover:via-rose-400 hover:to-pink-300",
      shadow: "shadow-pink-100",
    },
  ];

  // 🌀 تحميل أو لا يوجد بيانات
  if (productLoading)
    return (
      <section className="container mx-auto px-4 py-12 text-center">
        <Helmet>
          <title>Loading Categories - Bazaar</title>
        </Helmet>
        <div className="flex justify-center space-x-2">
          <div className="w-3 h-3 bg-pink-400 rounded-full animate-bounce"></div>
          <div className="w-3 h-3 bg-purple-400 rounded-full animate-bounce delay-100"></div>
          <div className="w-3 h-3 bg-rose-400 rounded-full animate-bounce delay-200"></div>
        </div>
      </section>
    );

  if (!categories.length && !isSectionOpen)
    return (
      <section className="container mx-auto px-4 py-8 text-center">
        <Helmet>
          <title>No Categories Available</title>
        </Helmet>
        <div className="text-gray-400">
          <svg
            className="w-16 h-16 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
            />
          </svg>
          <p className="text-lg">No categories available yet</p>
        </div>
      </section>
    );

  // إذا السيكشن مقفول، نعرض الزر فقط
  if (!isSectionOpen) {
    return (
      <section className="w-full px-4 py-8">
        <Helmet>
          <title>Shop by Category - Bazaar</title>
          <meta
            name="description"
            content="Browse our beautiful collection of product categories"
          />
        </Helmet>

        <div className="max-w-md mx-auto">
          {/* زر Shop by Category */}
          <button
            onClick={() => setIsSectionOpen(true)}
            className="w-full p-6 rounded-2xl bg-gradient-to-r from-pink-500 to-purple-500 text-white text-xl md:text-2xl font-bold shadow-lg hover:shadow-xl hover:from-pink-600 hover:to-purple-600 transition-all duration-300 transform hover:-translate-y-1 flex items-center justify-between group"
            aria-label="Open shop by category section"
          >
            <span>Shop by Category</span>
            <svg
              className="w-6 h-6 transform group-hover:rotate-90 transition-transform duration-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>

          {/* تيكست تاني تحت لو عايز */}
          {categories.length > 0 && (
            <div className="mt-4 text-center">
              <p className="text-gray-500 text-sm">
                Click to explore {categories.length} categories
              </p>
            </div>
          )}
        </div>
      </section>
    );
  }

  // إذا السيكشن مفتوح، نعرض كل الكاتيجوريات
  return (
    <section id="categories-section" className="w-full px-4 py-10 md:py-14">
      <Helmet>
        <title>Shop by Category - Bazaar</title>
        <meta
          name="description"
          content="Browse our beautiful collection of product categories. Find exactly what you're looking for."
        />
      </Helmet>

      {/* الهيدر مع زر الرجوع */}
      <div className="flex items-center justify-between mb-10 md:mb-14">
        <div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
            Shop by{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-500">
              Category
            </span>
          </h2>
          <p className="text-gray-500 text-lg">
            Discover products organized by categories
          </p>
        </div>

        <button
          onClick={() => setIsSectionOpen(false)}
          className="p-3 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-300"
          aria-label="Close categories section"
        >
          <svg
            className="w-6 h-6 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* جريند الكاتيجوريات */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
        {categories.map((category: Category, index: number) => {
          const catId = category.id.toString();
          const items = categoryProductsMap[category.id] || [];
          if (items.length === 0) return null;

          const currentIndex = currentImageIndex[catId] || 0;
          const fadeIn = fadeState[catId] ?? true;
          const usingCategoryImage = category.image && currentIndex === 0;

          const imageUrl = usingCategoryImage
            ? category.image
            : items[category.image ? currentIndex - 1 : currentIndex]?.product
                ?.images?.[0]?.image ||
              "https://placehold.co/400x400/FFE4E6/FB7185?text=No+Image";

          const totalDots = category.image ? items.length + 1 : items.length;

          // استخدام alt text من الصورة إن وجد
          const currentImage = usingCategoryImage
            ? null
            : items[category.image ? currentIndex - 1 : currentIndex]?.product
                ?.images?.[0];

          const altText = currentImage?.alt_text
            ? currentImage.alt_text
            : usingCategoryImage
            ? `${category.name} category image`
            : `Featured product in ${category.name} category`;

          // اختيار اللون بناءً على المؤشر
          const colorIndex = index % categoryColors.length;
          const colors = categoryColors[colorIndex];

          return (
            <article
              key={catId}
              className="relative bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 cursor-pointer transform hover:-translate-y-1 group"
              onClick={() => navigate(`/products?category=${category.slug}`)}
              tabIndex={0}
              role="button"
              aria-label={`${category.name} category with ${items.length} products`}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  navigate(`/products?category=${category.slug}`);
                }
              }}
            >
              {/* العلامة الدائرية */}
              <div
                className={`absolute top-4 left-4 z-20 w-10 h-10 rounded-full ${colors.bg} flex items-center justify-center shadow-lg border-2 border-white/80`}
              >
                <span className="text-white font-bold">
                  {category.name.charAt(0)}
                </span>
              </div>

              <figure className="h-56 md:h-64 relative overflow-hidden">
                <img
                  src={imageUrl}
                  alt={altText}
                  loading="lazy"
                  width={400}
                  height={400}
                  className={`
                    w-full 
                    h-full 
                    object-cover 
                    object-center
                    transition-all 
                    duration-700 
                    group-hover:scale-110
                    ${fadeIn ? "opacity-100" : "opacity-0"}
                  `}
                  onError={(e) => {
                    e.currentTarget.src =
                      "https://placehold.co/400x400/F0F9FF/3B82F6?text=Image+Error";
                    e.currentTarget.alt = "Failed to load image";
                  }}
                />

                {/* طبقة تدرج لوني شفاف */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent opacity-60 group-hover:opacity-70 transition-opacity duration-500" />

                {/* مؤشرات الصور */}
                {totalDots > 1 && (
                  <figcaption className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {[...Array(totalDots)].map((_, i) => (
                      <span
                        key={i}
                        className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                          i === currentIndex
                            ? `bg-white w-4 ${colors.shadow}`
                            : "bg-white/50"
                        }`}
                      />
                    ))}
                  </figcaption>
                )}
              </figure>

              {/* الجزء السفلي الملون */}
              <div
                className={`p-4 ${colors.bg} ${colors.text} transition-all duration-500 group-hover:${colors.hover}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-lg md:text-xl font-bold">
                    {category.name}
                  </h3>
                  <svg
                    className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm opacity-90">
                    {items.length} {items.length === 1 ? "product" : "products"}
                  </span>
                  <span className="text-xs px-2 py-0.5 bg-white/20 rounded-full backdrop-blur-sm">
                    Shop now
                  </span>
                </div>
              </div>

              {/* تأثير الهوفر */}
              <div className="absolute inset-0 border-2 border-transparent group-hover:border-white/30 rounded-2xl transition-all duration-500 pointer-events-none" />
            </article>
          );
        })}
      </div>

      {/* نص تشجيعي */}
      <div className="text-center mt-10 md:mt-12">
        <p className="text-gray-500 text-sm md:text-base">
          Click on any category to explore all products
        </p>
      </div>
    </section>
  );
};

export default CategoriesSection;